import type { HttpRequest, KeyValue } from '../../stores';

/**
 * Escape a string for use in a shell command
 * Uses single quotes and escapes single quotes within the string
 */
function shellEscape(str: string): string {
  // If the string contains single quotes, we need to escape them
  // Replace ' with '\'' (end quote, escaped quote, start quote)
  return `'${str.replace(/'/g, "'\\''")}'`;
}

/**
 * Build query string from enabled params
 */
function buildQueryString(params: KeyValue[]): string {
  const enabledParams = params.filter((p) => p.enabled && p.key);
  if (enabledParams.length === 0) return '';

  const queryParts = enabledParams.map(
    (p) => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value || '')}`
  );
  return `?${queryParts.join('&')}`;
}

/**
 * Build full URL with query params
 */
function buildFullUrl(url: string, params: KeyValue[]): string {
  const queryString = buildQueryString(params);
  // If URL already has query params, append with &
  if (queryString) {
    if (url.includes('?')) {
      return `${url}&${queryString.slice(1)}`;
    }
    return `${url}${queryString}`;
  }
  return url;
}

/**
 * Convert HttpRequest to cURL command
 */
export function requestToCurl(request: HttpRequest): string {
  const parts: string[] = ['curl'];

  // Method (GET is implicit in curl, but we'll include it for clarity)
  parts.push('-X', request.method);

  // Build URL with query params
  const fullUrl = buildFullUrl(request.url || '', request.params || []);
  parts.push(shellEscape(fullUrl));

  // Headers
  const enabledHeaders = (request.headers || []).filter((h) => h.enabled && h.key);
  for (const header of enabledHeaders) {
    parts.push('-H', shellEscape(`${header.key}: ${header.value || ''}`));
  }

  // Auth
  if (request.auth && request.auth.type !== 'none') {
    switch (request.auth.type) {
      case 'bearer':
        if (request.auth.bearer?.token) {
          parts.push('-H', shellEscape(`Authorization: Bearer ${request.auth.bearer.token}`));
        }
        break;
      case 'basic':
        if (request.auth.basic?.username) {
          const credentials = `${request.auth.basic.username}:${request.auth.basic.password || ''}`;
          parts.push('-u', shellEscape(credentials));
        }
        break;
      case 'api-key':
        if (request.auth.apiKey?.key && request.auth.apiKey?.value) {
          if (request.auth.apiKey.addTo === 'header') {
            parts.push('-H', shellEscape(`${request.auth.apiKey.key}: ${request.auth.apiKey.value}`));
          }
          // Query params are already handled in the URL building
        }
        break;
    }
  }

  // Body
  if (request.body && request.body.type !== 'none') {
    switch (request.body.type) {
      case 'raw':
        if (request.body.raw?.content) {
          // Add Content-Type header if not already present
          const hasContentType = enabledHeaders.some(
            (h) => h.key.toLowerCase() === 'content-type'
          );
          if (!hasContentType && request.body.raw.contentType) {
            const contentTypeMap: Record<string, string> = {
              json: 'application/json',
              xml: 'application/xml',
              text: 'text/plain',
            };
            const contentType = contentTypeMap[request.body.raw.contentType] || 'text/plain';
            parts.push('-H', shellEscape(`Content-Type: ${contentType}`));
          }
          parts.push('-d', shellEscape(request.body.raw.content));
        }
        break;

      case 'form-data':
        if (request.body.formData && request.body.formData.length > 0) {
          const enabledFormData = request.body.formData.filter((f) => f.enabled && f.key);
          for (const field of enabledFormData) {
            parts.push('-F', shellEscape(`${field.key}=${field.value || ''}`));
          }
        }
        break;

      case 'x-www-form-urlencoded':
        if (request.body.formData && request.body.formData.length > 0) {
          const enabledFormData = request.body.formData.filter((f) => f.enabled && f.key);
          if (enabledFormData.length > 0) {
            // Add Content-Type header if not already present
            const hasContentType = enabledHeaders.some(
              (h) => h.key.toLowerCase() === 'content-type'
            );
            if (!hasContentType) {
              parts.push('-H', shellEscape('Content-Type: application/x-www-form-urlencoded'));
            }
            const formBody = enabledFormData
              .map((f) => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value || '')}`)
              .join('&');
            parts.push('-d', shellEscape(formBody));
          }
        }
        break;
    }
  }

  return parts.join(' \\\n  ');
}

/**
 * Copy cURL command to clipboard
 */
export async function copyCurlToClipboard(request: HttpRequest): Promise<void> {
  const curl = requestToCurl(request);
  await navigator.clipboard.writeText(curl);
}

/**
 * Get cURL command as a single line (for display purposes)
 */
export function requestToCurlSingleLine(request: HttpRequest): string {
  const curl = requestToCurl(request);
  // Replace line continuations with spaces
  return curl.replace(/\s*\\\n\s*/g, ' ');
}
